import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, Loader2, Plus, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

interface LinkPatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  alreadyLinkedPatientIds: string[];
}

export function LinkPatientDialog({
  open,
  onOpenChange,
  companyId,
  alreadyLinkedPatientIds,
}: LinkPatientDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [linkingId, setLinkingId] = useState<string | null>(null);

  // Fetch all patients the manager has access to
  const { data: availablePatients = [], isLoading } = useQuery({
    queryKey: ['manager-available-patients', user?.id, alreadyLinkedPatientIds],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: links, error } = await supabase
        .from('patient_user_links')
        .select('patient_id, role')
        .eq('user_id', user.id)
        .eq('active', true)
        .eq('role', 'owner');

      if (error || !links) return [];

      // Filter out already linked patients
      const unlinkedIds = links
        .map(l => l.patient_id)
        .filter(id => !alreadyLinkedPatientIds.includes(id));

      if (unlinkedIds.length === 0) return [];

      const { data: patients, error: pError } = await supabase
        .from('patients')
        .select('id, nickname, photo_url')
        .in('id', unlinkedIds)
        .order('nickname');

      if (pError) return [];
      return patients || [];
    },
    enabled: open && !!user?.id,
  });

  const handleLink = async (patientId: string) => {
    setLinkingId(patientId);
    try {
      const { error } = await supabase
        .from('company_patient_assignments')
        .insert({
          company_id: companyId,
          patient_id: patientId,
          is_active: true,
        });

      if (error) throw error;

      track('company_patient_linked', { company_id: companyId, patient_id: patientId });

      toast.success(t('company.linkSuccess'));
      await queryClient.invalidateQueries({ queryKey: ['company-patients-shifts'] });
      await queryClient.invalidateQueries({ queryKey: ['manager-available-patients'] });
    } catch (error: any) {
      console.error('Error linking patient:', error);
      const msg = String(error?.message || '');
      if (msg.includes('company_patient_limit_exceeded')) {
        toast.error(t('subscription.patientLimit.blockedSubscribed'));
      } else {
        toast.error(t('company.linkError'));
      }
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            {t('company.linkPatient')}
          </DialogTitle>
          <DialogDescription>{t('company.linkPatientDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : availablePatients.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('company.noAvailablePatients')}</p>
            </div>
          ) : (
            availablePatients.map((patient) => (
              <div
                key={patient.id}
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors"
              >
                <SignedAvatar
                  src={patient.photo_url}
                  bucket="patient-photos"
                  className="h-10 w-10"
                  fallback={
                    <span className="bg-primary/10 text-primary text-sm flex items-center justify-center w-full h-full">
                      {patient.nickname.charAt(0).toUpperCase()}
                    </span>
                  }
                />
                <span className="flex-1 font-medium truncate">{patient.nickname}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={linkingId === patient.id}
                  onClick={() => handleLink(patient.id)}
                >
                  {linkingId === patient.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      {t('company.linkAction')}
                    </>
                  )}
                </Button>
              </div>
            ))
          )}
        </div>

        <Button
          variant="default"
          className="w-full"
          onClick={() => {
            onOpenChange(false);
            navigate('/app/patients/new');
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('company.createNewPatient')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
